import { startAuthentication, startRegistration } from "@simplewebauthn/browser"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import {
  requestOtp,
  requestRegistrationOtp,
  verifyOtp,
  verifyRegistrationOtp,
  type ClientOtpFlow,
} from "../api/endpoints/auth"
import { ApiError } from "../api/client"
import {
  authenticateWithWebauthn,
  getWebauthnAuthOptions,
  getWebauthnRegisterOptions,
  registerWebauthnCredential,
} from "../api/endpoints/portal"
import { setPortalLanguage } from "../i18n"
import { useAuth } from "../stores/auth"

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setSession } = useAuth()
  const [step, setStep] = useState<"phone" | "otp">("phone")
  const [phoneNumber, setPhoneNumber] = useState("+263")
  const [otp, setOtp] = useState("")
  const [otpFlow, setOtpFlow] = useState<ClientOtpFlow | null>(null)
  const [feedback, setFeedback] = useState("")
  const [error, setError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [language, setLanguage] = useState(
    () => window.localStorage.getItem("portal_language") ?? "en",
  )
  const [isBiometricSubmitting, setIsBiometricSubmitting] = useState(false)
  const rememberedBiometric =
    typeof window !== "undefined"
      ? window.localStorage.getItem("portal_biometric_client")
      : null
  const biometricClient = rememberedBiometric ? JSON.parse(rememberedBiometric) as {
    clientId: string
    fullName: string
    phoneNumber: string
  } : null

  useEffect(() => {
    setPortalLanguage(language)
  }, [language])

  function isExistingClientRegistrationConflict(error: unknown) {
    return (
      error instanceof ApiError &&
      error.code === "BUSINESS_RULE_VIOLATION" &&
      error.message.includes("Sign in instead")
    )
  }

  async function resolveClientOtpRequest(phone: string) {
    const response = await requestOtp(phone)

    if (response.data.flow) {
      return {
        flow: response.data.flow,
        message: response.data.message,
      }
    }

    try {
      const registrationResponse = await requestRegistrationOtp(phone)
      return {
        flow: "REGISTER" as const,
        message: registrationResponse.data.message,
      }
    } catch (error) {
      if (isExistingClientRegistrationConflict(error)) {
        return {
          flow: "LOGIN" as const,
          message: response.data.message,
        }
      }

      throw error
    }
  }

  async function maybeRegisterBiometric(client: {
    clientId: string
    fullName: string
    phoneNumber: string
  }) {
    if (!("PublicKeyCredential" in window)) return
    if (!window.confirm(t("profile.enableBiometric"))) return

    try {
      const response = await getWebauthnRegisterOptions()
      const registration = await startRegistration(response.data.options as any)
      await registerWebauthnCredential({
        challengeId: response.data.challengeId,
        response: registration as unknown as Record<string, unknown>,
      })

      window.localStorage.setItem("portal_biometric_client", JSON.stringify(client))
    } catch {
      // Fall back silently to OTP-only sign-in on unsupported or failed devices.
    }
  }

  async function handleRequestOtp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError("")
    setFeedback("")
    setIsSubmitting(true)

    try {
      const response = await resolveClientOtpRequest(phoneNumber)
      setFeedback(response.message)
      setOtpFlow(response.flow)
      setOtp("")
      setStep("otp")
    } catch (requestError) {
      setError((requestError as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleVerifyOtp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError("")
    setFeedback("")
    setIsSubmitting(true)

    try {
      if (!otpFlow) {
        setOtp("")
        setStep("phone")
        setError(t("login.requestNewOtp"))
        return
      }

      if (otpFlow === "REGISTER") {
        const response = await verifyRegistrationOtp(phoneNumber, otp)
        navigate("/register", {
          state: {
            phoneNumber,
            registrationToken: response.data.registrationToken,
            preferredLanguage: language,
          },
        })
        return
      }

      const response = await verifyOtp(phoneNumber, otp)
      setSession(response.data.client, response.data.expiresAt)
      await maybeRegisterBiometric({
        clientId: response.data.client.clientId,
        fullName: response.data.client.fullName,
        phoneNumber: response.data.client.phoneNumber,
      })
      navigate("/")
    } catch (verifyError) {
      setError((verifyError as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleBiometricLogin() {
    if (!biometricClient) return

    setIsBiometricSubmitting(true)
    setError("")
    setFeedback("")

    try {
      const options = await getWebauthnAuthOptions(biometricClient.clientId)
      const response = await startAuthentication(options.data.options as any)
      const authResponse = await authenticateWithWebauthn({
        clientId: biometricClient.clientId,
        challengeId: options.data.challengeId,
        response: response as unknown as Record<string, unknown>,
      })

      setSession(authResponse.data.client, authResponse.data.expiresAt)
      navigate("/")
    } catch {
      setError(t("login.biometricUnavailable"))
    } finally {
      setIsBiometricSubmitting(false)
    }
  }

  return (
    <div className="auth-shell">
      <section className="auth-card">
        <p className="eyebrow">{t("login.eyebrow")}</p>
        <h1>{t("login.title")}</h1>
        <p className="auth-copy">{t("login.copy")}</p>

        <label>
          {t("login.language")}
          <select onChange={(event) => setLanguage(event.target.value)} value={language}>
            <option value="en">English</option>
            <option value="sn">chiShona</option>
          </select>
        </label>

        {feedback ? <div className="status-banner success">{feedback}</div> : null}
        {error ? <div className="status-banner error">{error}</div> : null}

        {biometricClient ? (
          <button
            className="ghost-button"
            disabled={isBiometricSubmitting}
            onClick={() => void handleBiometricLogin()}
            type="button"
          >
            {isBiometricSubmitting ? t("login.verifyingOtp") : t("login.biometric")}
          </button>
        ) : null}

        {step === "phone" ? (
          <form className="auth-form" onSubmit={handleRequestOtp}>
            <label>
              {t("login.phone")}
              <input
                autoComplete="tel"
                inputMode="tel"
                onChange={(event) => setPhoneNumber(event.target.value)}
                placeholder="+263771234567"
                required
                type="tel"
                value={phoneNumber}
              />
            </label>
            <button className="primary-button" disabled={isSubmitting} type="submit">
              {isSubmitting ? t("login.sendingOtp") : t("login.requestOtp")}
            </button>
            <button
              className="ghost-button"
              onClick={() => navigate("/register")}
              type="button"
            >
              Create borrower account
            </button>
          </form>
        ) : (
          <form className="auth-form" onSubmit={handleVerifyOtp}>
            <label>
              {t("login.otp")}
              <input
                inputMode="numeric"
                maxLength={6}
                onChange={(event) => setOtp(event.target.value)}
                placeholder="123456"
                required
                value={otp}
              />
            </label>
            <button className="primary-button" disabled={isSubmitting} type="submit">
              {isSubmitting ? t("login.verifyingOtp") : t("login.verifyOtp")}
            </button>
            <button
              className="ghost-button"
              onClick={() => {
                setStep("phone")
                setOtp("")
                setOtpFlow(null)
              }}
              type="button"
            >
              {t("login.changePhone")}
            </button>
          </form>
        )}
      </section>
    </div>
  )
}
